import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, User, Sparkles } from 'lucide-react';
import { useProposalData, useProposal } from '../../context';
import { formatValidUntil } from '../../utils';

export function HeroSection() {
  const [photoError, setPhotoError] = useState(false);
  const proposal = useProposalData();
  const { daysUntilExpiry } = useProposal();
  const isServiceProposal = proposal.proposalType === 'service';

  const getExpiryStatus = () => {
    if (daysUntilExpiry === null) return { color: 'text-gray-400', label: '' };
    if (daysUntilExpiry <= 3) return { color: 'text-red-400', label: 'Expires soon!' };
    if (daysUntilExpiry <= 7) return { color: 'text-amber-400', label: `${daysUntilExpiry} days left` };
    return { color: 'text-green-400', label: `${daysUntilExpiry} days remaining` };
  };

  const expiryStatus = getExpiryStatus();

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background Layers */}
      <div className="absolute inset-0">
        {/* Base Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-hul-navy via-hul-navy-dark to-surface-darker" />

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(237, 44, 44, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(237, 44, 44, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Diagonal Accent */}
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-hul-red/10 rounded-full blur-3xl" />
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-hul-red/5 rounded-full blur-3xl" />

        {/* Industrial Corner Accent */}
        <svg
          className="absolute top-0 right-0 w-64 h-64 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon points="100,0 100,100 0,0" fill="currentColor" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Main Content */}
          <div>
            {/* Proposal ID Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Sparkles className="w-4 h-4 text-hul-red" />
              <span className="font-mono">{proposal.proposal.id}</span>
            </motion.div>

            {/* Greeting */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-wide mb-4">
                <span className="block text-white/60 text-3xl sm:text-4xl mb-2">
                  {isServiceProposal ? 'Service Estimate Prepared For' : 'Prepared for'}
                </span>
                {proposal.customer.company}
              </h1>
            </motion.div>

            {/* Contact Name */}
            <motion.p
              className="text-xl text-white/70 flex items-center gap-3 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <User className="w-5 h-5" />
              Attention: {proposal.customer.contact}
            </motion.p>

            {/* Validity */}
            <motion.div
              className="flex flex-wrap items-center gap-6 text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>Valid Until: {formatValidUntil(proposal.proposal.validUntil)}</span>
              </div>
              {expiryStatus.label && (
                <div className={`flex items-center gap-2 ${expiryStatus.color}`}>
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">{expiryStatus.label}</span>
                </div>
              )}
            </motion.div>

            {/* Custom Message */}
            {proposal.customMessage && (
              <motion.div
                className="mt-10 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <p className="text-white/80 leading-relaxed whitespace-pre-line">
                  {proposal.customMessage}
                </p>
              </motion.div>
            )}

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-wrap gap-4 mt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <a
                href={isServiceProposal ? '#tasks' : '#equipment'}
                className="btn-industrial"
              >
                {isServiceProposal ? 'View Tasks' : 'View Equipment'}
              </a>
              <a
                href={isServiceProposal ? '#estimate-summary' : '#pricing'}
                className="px-8 py-4 font-heading text-lg uppercase tracking-wider text-white border-2 border-white/30 hover:border-white hover:bg-white/10 transition-all"
                style={{
                  clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
                }}
              >
                {isServiceProposal ? 'See Summary' : 'See Pricing'}
              </a>
            </motion.div>
          </div>

          {/* Right: Rep Card */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-hul-red to-hul-red-dark p-6 text-white">
                <div className="text-sm uppercase tracking-wider opacity-80 mb-1">
                  Your Equipment Specialist
                </div>
                <div className="font-display text-2xl tracking-wide">
                  {proposal.salesperson.name}
                </div>
                {proposal.salesperson.title && (
                  <div className="text-white/80 mt-1">
                    {proposal.salesperson.title}
                  </div>
                )}
              </div>

              {/* Card Body */}
              <div className="p-6">
                <div className="flex items-start gap-4">
                  {/* Photo */}
                  {proposal.salesperson.photo && !photoError ? (
                    <img
                      src={proposal.salesperson.photo}
                      alt={proposal.salesperson.name}
                      className="w-20 h-20 rounded-xl object-cover border-4 border-gray-100"
                      onError={() => setPhotoError(true)}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-hul-navy flex items-center justify-center border-4 border-gray-100">
                      <span className="font-display text-3xl text-white">
                        {proposal.salesperson.name.charAt(0)}
                      </span>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="flex-1 space-y-3">
                    <a
                      href={`tel:${proposal.salesperson.phone}`}
                      className="flex items-center gap-3 text-hul-navy hover:text-hul-red transition-colors group"
                    >
                      <div className="w-10 h-10 bg-hul-navy/10 rounded-lg flex items-center justify-center group-hover:bg-hul-red/10 transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <span className="font-medium">{proposal.salesperson.phone}</span>
                    </a>

                    <a
                      href={`mailto:${proposal.salesperson.email}`}
                      className="flex items-center gap-3 text-hul-navy hover:text-hul-red transition-colors group"
                    >
                      <div className="w-10 h-10 bg-hul-navy/10 rounded-lg flex items-center justify-center group-hover:bg-hul-red/10 transition-colors">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="font-medium truncate">{proposal.salesperson.email}</span>
                    </a>
                  </div>
                </div>

                {/* Customer Address */}
                {proposal.customer.address && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="flex items-start gap-3 text-gray-600">
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">
                          Delivery Location
                        </div>
                        <div className="whitespace-pre-line">
                          {proposal.customer.address}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-hul-red/20 rounded-full blur-2xl" />
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        data-pdf-hide
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <motion.div
          className="w-8 h-12 border-2 border-white/30 rounded-full flex justify-center"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-2 h-3 bg-white/60 rounded-full mt-2"
            animate={{ opacity: [1, 0, 1], y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
}
